import { useState, useRef, useEffect } from "react";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, X, Send, RotateCcw, Check, Loader2, ListPlus, FolderPlus, SquarePlus, Crown, Zap } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const QUICK_PROMPTS = [
  "Resume el estado del Espacio",
  "¿Qué tareas están pendientes y quién las lleva?",
  "Resume la conversación reciente del chat",
  "¿Cómo van los gastos y presupuestos?",
];

const actionLabel = (a) => {
  if (a.type === "create_task") return { icon: SquarePlus, text: `Crear tarea "${a.title}"` };
  if (a.type === "create_subtasks") return { icon: ListPlus, text: `Crear ${a.titles?.length || 0} subtareas: ${(a.titles || []).slice(0, 4).join(", ")}${(a.titles?.length || 0) > 4 ? "…" : ""}` };
  if (a.type === "create_project") return { icon: FolderPlus, text: `Crear proyecto "${a.name}"${a.tasks?.length ? ` con ${a.tasks.length} tareas` : ""}` };
  return { icon: SquarePlus, text: "Acción propuesta" };
};

const ActionCard = ({ action, workspaceId, onExecuted }) => {
  const qc = useQueryClient();
  const [state, setState] = useState("idle");
  const { icon: Icon, text } = actionLabel(action);

  const execute = async () => {
    setState("loading");
    try {
      const { data } = await api.post(`/workspaces/${workspaceId}/ai/execute`, { action });
      toast.success(`Hecho: ${data.created}`);
      setState("done");
      qc.invalidateQueries();
      onExecuted?.();
    } catch (e) {
      toast.error(apiError(e));
      setState("idle");
    }
  };

  return (
    <div className="flex items-start gap-2 rounded-lg border border-primary/30 bg-primary/5 p-3" data-testid="ai-action-card">
      <Icon className="h-4 w-4 text-primary mt-0.5 shrink-0" />
      <p className="text-xs flex-1 leading-relaxed">{text}</p>
      {state === "done" ? (
        <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium flex items-center gap-1 shrink-0"><Check className="h-3 w-3" /> Hecho</span>
      ) : (
        <Button size="sm" className="h-7 px-2 text-xs shrink-0" disabled={state === "loading"} onClick={execute} data-testid="ai-action-confirm-btn">
          {state === "loading" ? <Loader2 className="h-3 w-3 animate-spin" /> : "Confirmar"}
        </Button>
      )}
    </div>
  );
};

export const AiAssistant = ({ workspaceId }) => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [quotaExhausted, setQuotaExhausted] = useState(false);
  const bottomRef = useRef(null);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: sub } = useQuery({
    queryKey: ["subscription-me"],
    queryFn: async () => (await api.get("/subscription/me")).data,
    enabled: open,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const totalRemaining = (sub?.ai_monthly_remaining || 0) + (sub?.ai_booster_credits || 0);
  const showLowBanner = sub && totalRemaining <= 3 && totalRemaining > 0 && sub.plan === "free";

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || sending) return;
    setInput("");
    setMessages((m) => [...m, { id: crypto.randomUUID(), role: "user", content: msg }]);
    setSending(true);
    try {
      const { data } = await api.post(`/workspaces/${workspaceId}/ai/chat`, { message: msg, session_id: sessionId });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { id: crypto.randomUUID(), role: "assistant", content: data.reply, actions: data.actions || [] }]);
      qc.invalidateQueries({ queryKey: ["subscription-me"] });
    } catch (e) {
      if (e?.response?.status === 402) {
        setQuotaExhausted(true);
        const detail = e.response.data?.detail || {};
        setMessages((m) => [...m, {
          id: crypto.randomUUID(),
          role: "assistant",
          content: detail.message || "Has agotado tus mensajes AI. Compra un Booster o pásate a Premium para seguir.",
          actions: [],
          quotaError: true,
        }]);
      } else {
        setMessages((m) => [...m, { id: crypto.randomUUID(), role: "assistant", content: apiError(e), actions: [] }]);
      }
    } finally {
      setSending(false);
    }
  };

  const reset = () => {
    setMessages([]);
    setSessionId(null);
    setQuotaExhausted(false);
  };

  return (
    <>
      <button
        data-testid="ai-assistant-fab"
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-40 right-4 md:bottom-24 md:right-6 z-40 h-12 w-12 rounded-2xl bg-gradient-to-br from-primary to-indigo-600 text-white shadow-lg shadow-primary/30 flex items-center justify-center hover:scale-105 transition-transform"
        title="Asistente IA"
      >
        {open ? <X className="h-5 w-5" /> : <Sparkles className="h-5 w-5" />}
      </button>

      {open && (
        <div
          data-testid="ai-assistant-panel"
          className="fixed z-50 inset-x-2 bottom-2 top-16 md:inset-auto md:right-6 md:bottom-[10.5rem] md:w-[420px] md:h-[560px] md:max-h-[calc(100vh-12rem)] rounded-2xl border border-border bg-card shadow-2xl flex flex-col overflow-hidden animate-fade-up"
        >
          <div className="flex items-center gap-2 px-4 h-14 border-b border-border/60 bg-gradient-to-r from-primary/10 to-indigo-500/10 shrink-0">
            <Sparkles className="h-4 w-4 text-primary" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-heading font-bold">Asistente IA</p>
              <p className="text-[10px] text-muted-foreground">
                {sub
                  ? `${totalRemaining} mensajes disponibles${sub.plan === "premium" ? " · Premium" : ""}`
                  : "Nunca modifica nada sin tu confirmación"}
              </p>
            </div>
            <Button variant="ghost" size="sm" className="h-8 px-2" onClick={reset} title="Nueva conversación" data-testid="ai-new-chat-btn">
              <RotateCcw className="h-3.5 w-3.5" />
            </Button>
            <Button variant="ghost" size="sm" className="h-8 px-2 md:hidden" onClick={() => setOpen(false)} data-testid="ai-close-btn">
              <X className="h-4 w-4" />
            </Button>
          </div>

          {(showLowBanner || quotaExhausted) && (
            <div
              className="px-4 py-2 border-b border-primary/20 bg-primary/5 flex items-center gap-2 text-xs"
              data-testid="ai-quota-banner"
            >
              <Crown className="h-3.5 w-3.5 text-primary shrink-0" />
              <span className="flex-1">
                {quotaExhausted
                  ? "Has agotado tus mensajes AI."
                  : `Te quedan solo ${totalRemaining} mensajes AI este mes.`}
              </span>
              <Button
                size="sm"
                className="h-6 px-2 text-[11px]"
                onClick={() => navigate("/subscription")}
                data-testid="ai-upgrade-btn"
              >
                {sub?.plan === "premium" ? <><Zap className="h-3 w-3 mr-1" />Booster</> : "Mejorar"}
              </Button>
            </div>
          )}

          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.length === 0 && (
              <div className="space-y-3 pt-4">
                <p className="text-sm text-muted-foreground text-center">
                  Pregúntame sobre tareas, proyectos, conversaciones, documentos o gastos de este Espacio. También puedo proponerte subtareas o proyectos.
                </p>
                <div className="space-y-2">
                  {QUICK_PROMPTS.map((p) => (
                    <button
                      key={p}
                      data-testid="ai-quick-prompt"
                      onClick={() => send(p)}
                      className="w-full text-left text-xs px-3 py-2 rounded-lg border border-border hover:border-primary/50 hover:bg-primary/5 transition-colors"
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m) => (
              <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] rounded-xl px-3 py-2 text-sm whitespace-pre-wrap leading-relaxed ${
                    m.role === "user" ? "bg-primary text-primary-foreground" : "bg-secondary"
                  }`}
                  data-testid={m.role === "user" ? "ai-msg-user" : "ai-msg-assistant"}
                >
                  {m.content}
                  {m.actions?.length > 0 && (
                    <div className="mt-2 space-y-2">
                      {m.actions.map((a, j) => (
                        <ActionCard key={`${m.id}-${j}`} action={a} workspaceId={workspaceId} />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="ai-thinking">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Pensando…
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          <div className="p-3 border-t border-border/60 shrink-0 flex items-end gap-2">
            <Textarea
              data-testid="ai-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              placeholder="Escribe tu pregunta…"
              className="min-h-[40px] max-h-28 resize-none text-sm"
              rows={1}
            />
            <Button size="sm" className="h-10 w-10 p-0 shrink-0" disabled={!input.trim() || sending} onClick={() => send()} data-testid="ai-send-btn">
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
};
